import React from "react";
import { formatFixed, formatDelta } from '../../utils/numberFormat';

// ---------------------------------------------------------------------------
// PerformanceIndexGauge — near-full-circle "tire" gauge for SPI, CPI, etc.
//
// Arc layout (300° sweep, 60° gap at bottom):
//   7 o'clock = value 0   (start of arc)
//   12 o'clock = value 1  (top, boundary between BEHIND / AHEAD)
//   5 o'clock = value 2   (end of arc)
//
// The "tire" fills from 0 up to the current value.
// Fill color is determined by which tolerance band the value falls into.
// Tolerance lines cut radially across the tire like slicing a donut.
// In the gap (5-to-7 o'clock): directional KPI indicator (▲ ▼ or ●).
// ---------------------------------------------------------------------------

export type PerformanceIndexGaugeProps = {
  currentValue: number;
  previousValue?: number | null;
  warningThreshold: number;
  criticalThreshold: number;
  metricLabel?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  primaryValue?: number | null;
  secondaryValue?: number | null;
  lastUpdatedDate?: string | null;
  /** Label shown on the left (low-value) side of the gauge. Default: "BEHIND" */
  lowLabel?: string;
  /** Label shown on the right (high-value) side of the gauge. Default: "AHEAD" */
  highLabel?: string;
};

/* --- colors -------------------------------------------------------------- */

const GREEN = "#2ecc71";
const YELLOW = "#f1c40f";
const RED = "#e74c3c";
const TRACK = "var(--border-subtle)";

function bandColorForValue(
  value: number,
  warn: number,
  crit: number,
): string {
  if (!Number.isFinite(value)) return "var(--text-muted)";
  if (value < crit) return RED;
  if (value < warn) return YELLOW;
  return GREEN;
}

/* --- arc geometry -------------------------------------------------------- */

const CX = 130;
const CY = 110;
const R = 72; // outer mid-radius of the arc (~5% smaller for label room)
const ARC_W = 19; // tire thickness

// Value-to-angle mapping:
//   value 0 → 240° (7 o'clock)
//   value 1 → 90°  (12 o'clock)
//   value 2 → -60° (5 o'clock, equiv 300°)
// In standard math coords (CCW from positive x-axis).
// Total sweep: 300°.
function valToAngle(v: number): number {
  const clamped = Math.max(0, Math.min(2, v));
  // 240° - v * 150° in degrees, convert to radians
  return ((240 - clamped * 150) * Math.PI) / 180;
}

// SVG coordinates: y-down, so negate sin
function pol(angleMath: number, r: number) {
  return {
    x: CX + r * Math.cos(angleMath),
    y: CY - r * Math.sin(angleMath),
  };
}

// Build an SVG arc path from value v1 to v2 at radius r.
// The arc goes clockwise on screen (from higher math-angle to lower).
function arcPath(v1: number, v2: number, r: number): string {
  const a1 = valToAngle(v1);
  const a2 = valToAngle(v2);
  const s = pol(a1, r);
  const e = pol(a2, r);
  // Sweep angle in degrees
  const sweepDeg = ((a1 - a2) * 180) / Math.PI;
  const largeArc = sweepDeg > 180 ? 1 : 0;
  // sweep-flag 1 = clockwise in SVG (since y is flipped, decreasing math angle = CW on screen)
  return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
}

/* --- component ----------------------------------------------------------- */

export default function PerformanceIndexGauge({
  currentValue,
  previousValue,
  warningThreshold,
  criticalThreshold,
  metricLabel,
  primaryLabel,
  secondaryLabel,
  primaryValue,
  secondaryValue,
  lastUpdatedDate,
  lowLabel = "BEHIND",
  highLabel = "AHEAD",
}: PerformanceIndexGaugeProps) {
  const warn = warningThreshold;
  const crit = criticalThreshold;
  const color = bandColorForValue(currentValue, warn, crit);
  const clampedValue = Math.max(0, Math.min(2, currentValue));

  const displayValue = Number.isFinite(currentValue)
    ? formatFixed(currentValue, 2)
    : "—";

  const prevDisplay =
    previousValue != null && Number.isFinite(previousValue)
      ? formatFixed(previousValue, 2)
      : null;
  const prevColor =
    previousValue != null && Number.isFinite(previousValue)
      ? bandColorForValue(previousValue, warn, crit)
      : "var(--border-default)";

  // Trend indicator
  let trendSymbol = "●"; // same
  let trendLabel = "No Change";
  if (previousValue != null && Number.isFinite(previousValue) && Number.isFinite(currentValue)) {
    const diff = currentValue - previousValue;
    if (diff > 0.005) {
      trendSymbol = "▲";
      trendLabel = "Improved";
    } else if (diff < -0.005) {
      trendSymbol = "▼";
      trendLabel = "Worsened";
    }
  }

  // Guide marks: 0, 1, 2 — placed well outside the tire for legibility
  const guideOffset = R + ARC_W / 2 + 18;
  const guide0 = pol(valToAngle(0), guideOffset);
  const guide1 = pol(valToAngle(1.0), guideOffset);
  const guide2 = pol(valToAngle(2), guideOffset);

  // Tolerance cut positions (inner and outer edge of tire)
  const rInner = R - ARC_W / 2 - 2;
  const rOuter = R + ARC_W / 2 + 2;
  // Label position for tolerance values — just outside the outer cut
  const rToleranceLabel = rOuter + 14;

  // Gap center (between 5 and 7 o'clock = value space below 0 and above 2)
  // The gap center is at 6 o'clock = 270° = -90° in math, which is directly below center
  const gapCenterY = CY + R + ARC_W / 2 + 14;

  // CSS variable colors for consistent theming
  const cssVarColors = {
    track: "var(--bg-hover)",
    textMuted: "var(--text-muted)",
    textTertiary: "var(--text-tertiary)",
    borderDefault: "var(--border-default)",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "6px 4px 4px",
        gap: 0,
        minWidth: 250,
        maxWidth: 300,
      }}
    >
      {/* Metric label */}
      {metricLabel ? (
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "var(--text-tertiary)",
            textTransform: "uppercase",
            letterSpacing: 0.8,
            marginBottom: 2,
            textAlign: "center",
          }}
        >
          {metricLabel}
        </div>
      ) : null}

      {/* SVG Gauge — compact height to leave room for threshold readout */}
      <svg
        viewBox="0 0 260 240"
        width="260"
        height="240"
        style={{ display: "block", overflow: "visible" }}
      >
        {/* ---- Background tire track (grey) ---- */}
        <path
          d={arcPath(0, 2, R)}
          fill="none"
          stroke={TRACK}
          strokeWidth={ARC_W}
          strokeLinecap="round"
        />

        {/* ---- Colored fill from 0 up to current SPI ---- */}
        {Number.isFinite(currentValue) && clampedValue > 0.01 ? (
          <path
            d={arcPath(0, clampedValue, R)}
            fill="none"
            stroke={color}
            strokeWidth={ARC_W}
            strokeLinecap="round"
            opacity={0.7}
          />
        ) : null}

        {/* ---- Tolerance cut lines (radial slices, no text) ---- */}
        {[crit, warn].map((tv) => {
          if (tv <= 0 || tv >= 2) return null;
          const a = valToAngle(tv);
          const inner = pol(a, rInner);
          const outer = pol(a, rOuter);
          const lineColor = tv === crit ? RED : YELLOW;
          return (
            <line
              key={tv}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke={lineColor}
              strokeWidth={2}
              opacity={0.85}
            />
          );
        })}

        {/* ---- 1.0 marker line (white, heavier) ---- */}
        {(() => {
          const a = valToAngle(1.0);
          const inner = pol(a, rInner);
          const outer = pol(a, rOuter);
          return (
            <line
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke="var(--border-strong)"
              strokeWidth={2.5}
            />
          );
        })()}

        {/* ---- Guide labels: 0, 1, 2 ---- */}
        <text
          x={guide0.x}
          y={guide0.y + 5}
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill="var(--text-tertiary)"
        >
          0
        </text>
        <text
          x={guide1.x}
          y={guide1.y}
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill="var(--text-secondary)"
        >
          1
        </text>
        <text
          x={guide2.x}
          y={guide2.y + 5}
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill="var(--text-tertiary)"
        >
          2
        </text>

        {/* ---- Low / High pills flanking the "1" marker ---- */}
        {(() => {
          const isHigh = Number.isFinite(currentValue) && currentValue >= 1;
          const isLow = Number.isFinite(currentValue) && currentValue < 1;
          const pillW = 72;
          const pillH = 22;
          const pillR = 11;
          // Fixed center points for text — same in both states
          const lowCenterX = guide1.x - 24 - pillW / 2;
          const highCenterX = guide1.x + 24 + pillW / 2;
          const textY = guide1.y - 4;
          const pillY = textY - pillH / 2 - 3;
          return (
            <>
              {/* Low-side label (BEHIND / OVER / etc.) */}
              {isLow ? (
                <g>
                  <rect x={lowCenterX - pillW / 2} y={pillY} width={pillW} height={pillH} rx={pillR} fill={color} opacity={0.25} />
                  <rect x={lowCenterX - pillW / 2} y={pillY} width={pillW} height={pillH} rx={pillR} fill="none" stroke={color} strokeWidth={1} opacity={0.5} />
                  <text x={lowCenterX} y={textY} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="1.0" fill={color}>{lowLabel}</text>
                </g>
              ) : (
                <text x={lowCenterX} y={textY} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="1.0" fill="var(--text-muted)">{lowLabel}</text>
              )}
              {/* High-side label (AHEAD / UNDER / etc.) */}
              {isHigh ? (
                <g>
                  <rect x={highCenterX - pillW / 2} y={pillY} width={pillW} height={pillH} rx={pillR} fill={color} opacity={0.25} />
                  <rect x={highCenterX - pillW / 2} y={pillY} width={pillW} height={pillH} rx={pillR} fill="none" stroke={color} strokeWidth={1} opacity={0.5} />
                  <text x={highCenterX} y={textY} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="1.0" fill={color}>{highLabel}</text>
                </g>
              ) : (
                <text x={highCenterX} y={textY} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="1.0" fill="var(--text-muted)">{highLabel}</text>
              )}
            </>
          );
        })()}

        {/* ---- Current SPI value (large, centered) ---- */}
        <text
          x={CX}
          y={CY - 6}
          textAnchor="middle"
          fontSize="40"
          fontWeight="800"
          letterSpacing="-0.6"
          fill={color}
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {displayValue}
        </text>

        {/* ---- PREVIOUS label + value (independently color-coded) ---- */}
        {prevDisplay ? (
          <>
            <text
              x={CX}
              y={CY + 20}
              textAnchor="middle"
              fontSize="12"
              fontWeight="600"
              letterSpacing="0.5"
              fill="var(--text-muted)"
            >
              PREVIOUS:
            </text>
            <text
              x={CX}
              y={CY + 38}
              textAnchor="middle"
              fontSize="16"
              fontWeight="700"
              fill={prevColor}
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {prevDisplay}
            </text>
          </>
        ) : null}

        {/* ---- Trend indicator in the gap (between 5 and 7 o'clock) ---- */}
        <text
          x={CX}
          y={gapCenterY}
          textAnchor="middle"
          fontSize="40"
          fontWeight="800"
          fill={color}
        >
          {trendSymbol}
        </text>
      </svg>

      {/* Tolerance thresholds — label left, value right */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          padding: "0 16px",
          marginTop: -21,
          borderTop: "1px solid var(--border-subtle)",
          paddingTop: 6,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 3, backgroundColor: YELLOW, borderRadius: 1 }} />
            <span style={{ fontSize: 12, color: "var(--text-tertiary)", fontWeight: 500 }}>
              Warning
            </span>
          </div>
          <span style={{ fontSize: 13, color: YELLOW, fontWeight: 700, fontVariantNumeric: "tabular-nums" as const }}>
            {formatFixed(warn, 2)}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 10, height: 3, backgroundColor: RED, borderRadius: 1 }} />
            <span style={{ fontSize: 12, color: "var(--text-tertiary)", fontWeight: 500 }}>
              Critical
            </span>
          </div>
          <span style={{ fontSize: 13, color: RED, fontWeight: 700, fontVariantNumeric: "tabular-nums" as const }}>
            {formatFixed(crit, 2)}
          </span>
        </div>
      </div>

      {/* Last updated */}
      {lastUpdatedDate ? (
        <div
          style={{
            fontSize: 10,
            color: "var(--text-tertiary)",
            textAlign: "center",
            marginTop: 2,
          }}
        >
          Last Updated: {lastUpdatedDate}
        </div>
      ) : null}
    </div>
  );
}
